/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useContext, useEffect, useMemo } from 'react';
import { WIDGETS } from '/widgets';
import { LAYOUT_OPTIONS } from 'views/explorer/layoutOptions';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import isEmpty from 'lodash-es/isEmpty';
import AppCard from 'views/explorer/cardWrapper';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';

export const MiniExplorer = ({ layoutOptions = undefined, hideHeader }) => {
	const {
		state: {
			explorer: { layout, selectedDefinitionName },
			transient: { widgets },
		},
		dispatch
	} = useContext(WorkspaceContext);

	const layoutStyle = layoutOptions ? layoutOptions[layout.layoutStyle.key] : LAYOUT_OPTIONS[layout.layoutStyle.key];
	const styles = layoutStyle?.styles;

	// Only render widgets that are used by this application
	// This needs to update the transient widgets, which should be used to create the correct
	// cards.  This is needed to support the project specific widget ordering.
	const availableWidgets = useMemo(() => {
		return widgets
			.map((widget) => WIDGETS[widget.key])
			.filter((widget) => typeof widget.isValid === 'function' && widget.isValid(selectedDefinitionName));
	}, [widgets, selectedDefinitionName]);

	// reset the layout if there's an invalid widget selected 
	// helps refresh the explorer when swapping between different dil definitions with different widgets available
	useEffect(() => {
		if (layout.cards.filter(card => !availableWidgets.some(x => x.key === card.type)).length > 0) {
			dispatch(WorkspaceActionCreator.createNewExplorer());
		}
	}, [availableWidgets, dispatch, layout.cards]);

	if (isEmpty(availableWidgets)) {
		return <div />;
	}

	return (
		<div cy-test="widget-wrapper" css={[wrapperBaseStyle, styles]}>
			{layout.cards.map((card, i) => (
				<AppCard
					key={`${i}-${card.type}`}
					card={card}
					cardIndex={i}
					availableWidgets={availableWidgets}
					layoutOptions={layoutOptions}
					hideSave={true}
					hideHeader={hideHeader}
				/>
			))}
		</div>
	);
};

const wrapperBaseStyle = css`
	width: 100%;
	height: 100%;
	display: grid;
	grid-column-gap: 8px;
	grid-row-gap: 8px;
	// Ensure Layout Wrapper resizes
	padding: 1px; // So the box shadows still look good.
	& > * {
		overflow: hidden; // required to make things shrink properly.
	}
`;
